import { AnswerHeadline } from "@nimit9/signet-ui/components/answer-headline"
import { Button } from "@nimit9/signet-ui/components/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@nimit9/signet-ui/components/dialog"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@nimit9/signet-ui/components/drawer"
import { EmptyState } from "@nimit9/signet-ui/components/empty-state"
import { ErrorState } from "@nimit9/signet-ui/components/error-state"
import { Input } from "@nimit9/signet-ui/components/input"
import { Label } from "@nimit9/signet-ui/components/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@nimit9/signet-ui/components/select"
import { Skeleton } from "@nimit9/signet-ui/components/skeleton"
import { StatStrip } from "@nimit9/signet-ui/components/stat-strip"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nimit9/signet-ui/components/table"
import { useMediaQuery } from "@nimit9/signet-ui/hooks/use-media-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useId, useMemo, useRef, useState } from "react"
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts"
import {
  AXIS_PROPS,
  CategoryLegend,
  CHART_MARGIN,
  ChartCard,
  type ChartConfig,
  ChartFrame,
  ChartTooltip,
  GRID_PROPS,
  LINE_CURSOR,
  legendItemsFromConfig,
  MoneyTooltip,
  Y_AXIS_WIDTH,
} from "@/components/charts"
import { Amount } from "@/components/common/amount"
import { Header } from "@/components/layout/header"
import { PageWrapper } from "@/components/layout/page-wrapper"
import { useCompactCurrencyFormat } from "@/hooks/use-compact-currency-format"
import { useInvestments } from "@/hooks/use-investments"
import { type NetWorthSnapshot, useNetWorth } from "@/hooks/use-net-worth"
import { renderAnswerAmount, toSignetHeadline } from "@/lib/answer-headline"
import { api } from "@/lib/api"
import { buildNetWorthHeadline, MONTHS, snapshotLabel } from "./-headline"

export const Route = createFileRoute("/net-worth/")({
  component: NetWorthPage,
})

const MONTH_ITEMS = Object.fromEntries(MONTHS.map((m, i) => [String(i + 1), m]))

/** Token colours only — the chart flips with light/dark like every other chart. */
const CHART_CONFIG = {
  netWorth: { label: "Net worth", color: "var(--accent-primary)" },
  investments: { label: "Investments", color: "var(--chart-2)" },
  liquid: { label: "Liquid", color: "var(--text-muted)" },
} satisfies ChartConfig

const num = (v: string | null | undefined) => {
  const n = parseFloat(v ?? "0")
  return Number.isFinite(n) ? n : 0
}

interface SnapshotForm {
  month: number
  year: number
  liquidCash: string
  investments: string
  emergencyFund: string
  liabilities: string
}

const AMOUNT_FIELDS = [
  { key: "liquidCash" as const, label: "Liquid Cash (bank balances)" },
  { key: "investments" as const, label: "Investments (MF, stocks, FDs, PF)" },
  { key: "emergencyFund" as const, label: "Emergency Fund" },
  { key: "liabilities" as const, label: "Liabilities (loans, CC dues)" },
]

function SnapshotFormBody({ onDone }: { onDone: () => void }) {
  const id = useId()
  const now = new Date()
  const queryClient = useQueryClient()
  const [form, setForm] = useState<SnapshotForm>({
    month: now.getMonth() + 1,
    year: now.getFullYear(),
    liquidCash: "",
    investments: "",
    emergencyFund: "",
    liabilities: "0",
  })

  const set = (k: keyof SnapshotForm, v: string | number) => {
    touched.current = true
    setForm((f) => ({ ...f, [k]: v }))
  }

  // Start from what Paisa already knows (UAT J7-04): the last snapshot's cash,
  // emergency fund and liabilities, and today's portfolio value. All editable.
  const { data: nw } = useNetWorth()
  const { data: inv } = useInvestments()
  const touched = useRef(false)
  useEffect(() => {
    if (touched.current) return
    const last = nw?.data?.current
    const portfolio = inv?.summary?.totalCurrent
    if (!last && !portfolio) return
    setForm((f) => ({
      ...f,
      liquidCash: last ? String(Math.round(Number(last.liquidCash))) : f.liquidCash,
      emergencyFund: last ? String(Math.round(Number(last.emergencyFund))) : f.emergencyFund,
      liabilities: last ? String(Math.round(Number(last.liabilities))) : f.liabilities,
      investments: portfolio
        ? String(Math.round(portfolio))
        : last
          ? String(Math.round(Number(last.investments)))
          : f.investments,
    }))
  }, [nw, inv])

  const totalNetWorth =
    (parseFloat(form.liquidCash) || 0) +
    (parseFloat(form.investments) || 0) +
    (parseFloat(form.emergencyFund) || 0) -
    (parseFloat(form.liabilities) || 0)

  const mutation = useMutation({
    mutationFn: () =>
      api.post("/net-worth", {
        month: form.month,
        year: form.year,
        liquidCash: form.liquidCash || "0",
        investments: form.investments || "0",
        emergencyFund: form.emergencyFund || "0",
        liabilities: form.liabilities || "0",
        totalNetWorth: String(totalNetWorth),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["net-worth"] })
      onDone()
    },
  })

  return (
    <div data-testid="snapshot-form" className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label htmlFor={`${id}-month`}>Month</Label>
          <Select
            value={String(form.month)}
            onValueChange={(v) => v && set("month", +v)}
            items={MONTH_ITEMS}
          >
            <SelectTrigger id={`${id}-month`} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTHS.map((m, i) => (
                <SelectItem key={m} value={String(i + 1)}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label htmlFor={`${id}-year`}>Year</Label>
          <Input
            id={`${id}-year`}
            type="number"
            inputMode="numeric"
            value={form.year}
            min={2020}
            max={2100}
            onChange={(e) => set("year", +e.target.value)}
          />
        </div>
      </div>

      {AMOUNT_FIELDS.map(({ key, label }) => (
        <div key={key} className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-${key}`}>{label}</Label>
          <Input
            id={`${id}-${key}`}
            aria-label={label}
            type="number"
            inputMode="decimal"
            placeholder="0"
            value={form[key]}
            min={0}
            onChange={(e) => set(key, e.target.value)}
          />
        </div>
      ))}

      <div className="flex items-center justify-between gap-3 rounded-lg bg-bg-elevated px-4 py-3">
        <span className="text-caption text-text-muted">Total Net Worth</span>
        <span data-testid="snapshot-total">
          <Amount value={totalNetWorth} className="text-callout font-semibold text-text-primary" />
        </span>
      </div>

      <Button className="w-full" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
        {mutation.isPending ? "Saving…" : "Save Snapshot"}
      </Button>
      {mutation.isError && (
        <p role="alert" className="text-caption text-destructive">
          {mutation.error instanceof Error ? mutation.error.message : "Failed to save"}
        </p>
      )}
    </div>
  )
}

/** Phone: bottom Drawer; desktop: centred Dialog. Same form either way. */
function LogSnapshotSheet({
  open,
  onClose,
  isDesktop,
}: {
  open: boolean
  onClose: () => void
  isDesktop: boolean
}) {
  if (!isDesktop) {
    return (
      <Drawer open={open} onOpenChange={(o) => !o && onClose()} showSwipeHandle>
        <DrawerContent>
          <DrawerHeader className="pb-3">
            <DrawerTitle>Log Net Worth Snapshot</DrawerTitle>
          </DrawerHeader>
          <div className="flex-1 overflow-y-auto px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
            {open && <SnapshotFormBody onDone={onClose} />}
          </div>
        </DrawerContent>
      </Drawer>
    )
  }
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90dvh] max-w-sm overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Log Net Worth Snapshot</DialogTitle>
        </DialogHeader>
        {open && <SnapshotFormBody onDone={onClose} />}
      </DialogContent>
    </Dialog>
  )
}

/** Desktop: every snapshot as a table row, newest first. */
function SnapshotHistoryTable({ history }: { history: NetWorthSnapshot[] }) {
  return (
    <Table data-testid="snapshot-table">
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead className="pl-4">Month</TableHead>
          <TableHead className="text-right">Liquid</TableHead>
          <TableHead className="text-right">Investments</TableHead>
          <TableHead className="text-right">Emergency</TableHead>
          <TableHead className="text-right">Liabilities</TableHead>
          <TableHead className="pr-4 text-right">Net Worth</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {history.map((s) => {
          const liab = num(s.liabilities)
          return (
            <TableRow key={s.id} data-testid="snapshot-row">
              <TableCell className="pl-4 text-text-primary">{snapshotLabel(s)}</TableCell>
              <TableCell className="text-right text-text-secondary">
                <Amount value={s.liquidCash} />
              </TableCell>
              <TableCell className="text-right text-text-secondary">
                <Amount value={s.investments} />
              </TableCell>
              <TableCell className="text-right text-text-secondary">
                <Amount value={s.emergencyFund} />
              </TableCell>
              <TableCell className="text-right text-text-secondary">
                <Amount value={liab > 0 ? -liab : 0} />
              </TableCell>
              <TableCell className="pr-4 text-right font-medium text-text-primary">
                <Amount value={s.totalNetWorth} />
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

/** Phone: one stacked row per snapshot — month + net worth, the parts underneath. */
function SnapshotHistoryRows({ history }: { history: NetWorthSnapshot[] }) {
  const fmt = useCompactCurrencyFormat()
  return (
    <ul data-testid="snapshot-rows" className="divide-y divide-border-subtle">
      {history.map((s) => {
        const liab = num(s.liabilities)
        return (
          <li key={s.id} data-testid="snapshot-row" className="flex flex-col gap-1 px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <span className="text-body text-text-primary">{snapshotLabel(s)}</span>
              <Amount value={s.totalNetWorth} className="text-body font-semibold" />
            </div>
            <p className="text-caption text-text-muted">
              Liquid {fmt(num(s.liquidCash))} · Inv {fmt(num(s.investments))} · EF{" "}
              {fmt(num(s.emergencyFund))}
              {liab > 0 && ` · Debt −${fmt(liab)}`}
            </p>
          </li>
        )
      })}
    </ul>
  )
}

function NetWorthPage() {
  const fmtCompact = useCompactCurrencyFormat()
  const isDesktop = useMediaQuery("(min-width: 768px)")
  const { data, isLoading, isError, error, refetch } = useNetWorth()
  const [dialogOpen, setDialogOpen] = useState(false)

  const current = data?.data.current
  const history = useMemo(() => data?.data.history ?? [], [data])
  const headline = useMemo(() => buildNetWorthHeadline(current, history), [current, history])

  const chartData = useMemo(
    () =>
      [...history].reverse().map((s) => ({
        label: snapshotLabel(s),
        netWorth: num(s.totalNetWorth),
        investments: num(s.investments),
        liquid: num(s.liquidCash),
      })),
    [history],
  )

  const logAction = (
    <Button size="sm" onClick={() => setDialogOpen(true)}>
      + Log snapshot
    </Button>
  )
  const sheet = (
    <LogSnapshotSheet
      open={dialogOpen}
      onClose={() => setDialogOpen(false)}
      isDesktop={isDesktop}
    />
  )

  if (isLoading) {
    return (
      <>
        <Header title="Net Worth" showPeriod={false} action={logAction} />
        <PageWrapper>
          <div className="flex flex-col gap-4">
            {[1, 2].map((i) => (
              <Skeleton key={i} className="h-40 w-full animate-pulse rounded-xl" />
            ))}
          </div>
        </PageWrapper>
        {sheet}
      </>
    )
  }

  if (isError) {
    return (
      <>
        <Header title="Net Worth" showPeriod={false} action={logAction} />
        <PageWrapper>
          <ErrorState
            message={error instanceof Error ? error.message : undefined}
            onRetry={refetch}
          />
        </PageWrapper>
        {sheet}
      </>
    )
  }

  const liabilities = num(current?.liabilities)

  return (
    <>
      <Header title="Net Worth" showPeriod={false} action={logAction} />
      <PageWrapper>
        <div
          data-testid="net-worth-page"
          data-layout={isDesktop ? "desktop" : "phone"}
          className="flex min-w-0 flex-col gap-4 md:gap-5"
        >
          {headline && (
            <AnswerHeadline
              headline={toSignetHeadline(headline)}
              renderValue={renderAnswerAmount}
            />
          )}

          {current ? (
            <StatStrip
              isDesktop={isDesktop}
              className="lg:grid-cols-5"
              items={[
                { label: "Total Net Worth", value: <Amount value={current.totalNetWorth} /> },
                { label: "Liquid Cash", value: <Amount value={current.liquidCash} /> },
                { label: "Investments", value: <Amount value={current.investments} /> },
                { label: "Emergency Fund", value: <Amount value={current.emergencyFund} /> },
                {
                  label: "Liabilities",
                  value: <Amount value={liabilities > 0 ? -liabilities : 0} />,
                },
              ]}
            />
          ) : (
            <EmptyState
              title="No snapshot yet"
              description="Log your first net worth snapshot to get started"
              action={<Button onClick={() => setDialogOpen(true)}>Log first snapshot</Button>}
            />
          )}

          {chartData.length >= 2 && (
            <ChartCard
              data-testid="net-worth-chart"
              title="Net Worth Over Time"
              legend={<CategoryLegend items={legendItemsFromConfig(CHART_CONFIG)} />}
            >
              <ChartFrame config={CHART_CONFIG} height="lg">
                <LineChart data={chartData} margin={CHART_MARGIN} title="Net worth over time">
                  <CartesianGrid {...GRID_PROPS} />
                  <XAxis dataKey="label" {...AXIS_PROPS} minTickGap={16} />
                  <YAxis {...AXIS_PROPS} width={Y_AXIS_WIDTH} tickFormatter={fmtCompact} />
                  <ChartTooltip content={<MoneyTooltip showTotal={false} />} cursor={LINE_CURSOR} />
                  <Line
                    type="monotone"
                    dataKey="netWorth"
                    name={CHART_CONFIG.netWorth.label}
                    stroke="var(--color-netWorth)"
                    strokeWidth={2}
                    dot={{ fill: "var(--color-netWorth)", r: 3 }}
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="investments"
                    name={CHART_CONFIG.investments.label}
                    stroke="var(--color-investments)"
                    strokeWidth={1.5}
                    dot={false}
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="liquid"
                    name={CHART_CONFIG.liquid.label}
                    stroke="var(--color-liquid)"
                    strokeWidth={1.5}
                    dot={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ChartFrame>
            </ChartCard>
          )}

          {history.length > 0 && (
            <section
              data-testid="snapshot-history"
              className="flex min-w-0 flex-col gap-3 rounded-xl bg-bg-surface py-4 ring-1 ring-border-subtle"
            >
              <h2 className="px-4 text-callout font-semibold text-text-primary">
                Snapshot History
              </h2>
              {isDesktop ? (
                <div
                  className="overflow-x-auto"
                  role="region"
                  aria-label="Snapshot history"
                  // biome-ignore lint/a11y/noNoninteractiveTabindex: a scroll region must be reachable by keyboard
                  tabIndex={0}
                >
                  <SnapshotHistoryTable history={history} />
                </div>
              ) : (
                <SnapshotHistoryRows history={history} />
              )}
            </section>
          )}
        </div>
      </PageWrapper>
      {sheet}
    </>
  )
}
